import React from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Divider,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import DescriptionIcon from '@mui/icons-material/Description';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';

const Downloads = () => {
  // Sample download items - replace with actual data from API
  const downloadCategories = [
    {
      title: 'Brochures & Flyers',
      icon: <DescriptionIcon sx={{ fontSize: 40, color: '#f97316' }} />,
      items: [
        { name: 'GBCN Foundation Brochure 2024', type: 'PDF', size: '2.5 MB', url: '#' },
        { name: 'Annual Report 2023', type: 'PDF', size: '5.2 MB', url: '#' },
        { name: 'Event Flyer Template', type: 'PDF', size: '1.8 MB', url: '#' },
      ],
    },
    {
      title: 'Forms & Applications',
      icon: <PictureAsPdfIcon sx={{ fontSize: 40, color: '#f97316' }} />,
      items: [
        { name: 'Membership Application Form', type: 'PDF', size: '500 KB', url: '#' },
        { name: 'Volunteer Registration Form', type: 'PDF', size: '450 KB', url: '#' },
        { name: 'Event Participation Form', type: 'PDF', size: '380 KB', url: '#' },
        { name: 'Donation Receipt Template', type: 'PDF', size: '320 KB', url: '#' },
      ],
    },
    {
      title: 'Images & Media',
      icon: <ImageIcon sx={{ fontSize: 40, color: '#f97316' }} />,
      items: [
        { name: 'GBCN Logo Pack', type: 'ZIP', size: '8.5 MB', url: '#' },
        { name: 'Event Photos Collection', type: 'ZIP', size: '25 MB', url: '#' },
        { name: 'Social Media Templates', type: 'ZIP', size: '12 MB', url: '#' },
      ],
    },
    {
      title: 'Videos',
      icon: <VideoLibraryIcon sx={{ fontSize: 40, color: '#f97316' }} />,
      items: [
        { name: 'Foundation Introduction Video', type: 'MP4', size: '45 MB', url: '#' },
        { name: 'Annual Event Highlights 2023', type: 'MP4', size: '120 MB', url: '#' },
      ],
    },
  ];

  const handleDownload = (url, name) => {
    // In a real application, this would trigger an actual download
    // For now, it's a placeholder
    if (url !== '#') {
      window.open(url, '_blank');
    } else {
      alert(`Download functionality for "${name}" will be implemented soon.`);
    }
  };

  return (
    <Box sx={{ minHeight: '80vh', py: 8, backgroundColor: '#ffffff' }}>
      <Container maxWidth="lg">
        <Typography
          variant="h2"
          component="h1"
          sx={{
            textAlign: 'center',
            fontWeight: 700,
            mb: 2,
            color: '#f97316',
          }}
        >
          Downloads
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ textAlign: 'center', mb: 6, maxWidth: '600px', mx: 'auto' }}
        >
          Access brochures, forms, media files, and other resources from GBCN Foundation.
        </Typography>

        <Grid container spacing={4}>
          {downloadCategories.map((category, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Card
                sx={{
                  height: '100%',
                  backgroundColor: '#ffffff',
                  border: '1px solid #e5e7eb',
                  transition: 'transform 0.3s, box-shadow 0.3s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 24px rgba(249, 115, 22, 0.15)',
                    borderColor: '#f97316',
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    {category.icon}
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 600,
                        color: '#111827',
                        ml: 2,
                      }}
                    >
                      {category.title}
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 3 }} />
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {category.items.map((item, itemIndex) => (
                      <Box
                        key={itemIndex}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          p: 2,
                          backgroundColor: '#f9fafb',
                          borderRadius: 2,
                          '&:hover': {
                            backgroundColor: '#fff7ed',
                          },
                        }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: 500, color: '#111827', mb: 0.5 }}
                          >
                            {item.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {item.type} • {item.size}
                          </Typography>
                        </Box>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<DownloadIcon />}
                          onClick={() => handleDownload(item.url, item.name)}
                          sx={{
                            backgroundColor: '#f97316',
                            '&:hover': { backgroundColor: '#ea580c' },
                            ml: 2,
                          }}
                        >
                          Download
                        </Button>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ mt: 6, p: 3, backgroundColor: '#fff7ed', borderRadius: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#111827' }}>
            Need Help?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            If you're having trouble downloading any file or need assistance, please contact us at{' '}
            <Box
              component="span"
              sx={{ color: '#f97316', fontWeight: 600, textDecoration: 'none' }}
            >
              gbcnfoundation@gmail.com
            </Box>
            {' '}or call us at{' '}
            <Box
              component="span"
              sx={{ color: '#f97316', fontWeight: 600 }}
            >
              +91-8800656660
            </Box>
            .
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Downloads;

